/**
 * Brand primitives. The reference's signature pieces, built for DrTutor.
 * Spec: `brand-redesign-docs/04-SURFACES-AND-COMPONENTS.md`.
 *
 *   HeroPanel       deep teal panel that opens a page
 *   TagChip         the small sand label ("TODAY", "3 NEW")
 *   SectionHeading  a title with a muted inline caption and an optional action
 *   ActionTiles     a row of quick-action tiles on tinted gradient art
 *   FeatureBanners  one or two deep banners, each promoting a real next step
 *
 * Every piece uses the app's existing type steps. None fetches data: callers
 * pass what they already have, so nothing here reaches the backend.
 */

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { SkeletonLine } from "@/primitives/ui/Skeleton";

import "./brand.css";

/* ------------------------------------------------------------ TagChip */

export function TagChip({ children, tone = "sand", dot = false }: { children: ReactNode; tone?: "sand" | "mint"; dot?: boolean }) {
  return (
    <span className="br-tag" data-tone={tone === "sand" ? undefined : tone}>
      {dot ? <span className="br-tag__dot" aria-hidden /> : null}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------ HeroPanel */

export interface HeroPanelProps {
  /** The heading. A string renders as the page's `h1` in the greeting step. */
  title: ReactNode;
  /** Sand tag chip above the heading. */
  tag?: ReactNode;
  /** A control beside the tag, e.g. a parent's child switcher. */
  topAside?: ReactNode;
  summary?: ReactNode;
  /** While the summary loads: how many lines to hold, as line boxes in the
   *  real summary paragraph. Ignored once `summary` is set. */
  summarySkeleton?: number;
  /** Status chips (StatusText) under the summary. */
  meta?: ReactNode;
  /** Buttons: `inverse` for the primary, `glass` for the rest. */
  actions?: ReactNode;
  /** Right-hand visual, built from data the page already has. */
  aside?: ReactNode;
  size?: "home" | "page";
  tone?: "teal" | "sand";
  className?: string;
  "aria-label"?: string;
}

export function HeroPanel({
  title,
  tag,
  topAside,
  summary,
  summarySkeleton = 0,
  meta,
  actions,
  aside,
  size = "page",
  tone = "teal",
  className,
  "aria-label": ariaLabel,
}: HeroPanelProps) {
  return (
    <section
      className={className ? `br-hero ${className}` : "br-hero"}
      data-size={size}
      data-tone={tone === "teal" ? undefined : tone}
      aria-label={ariaLabel}
    >
      <div className="br-hero__lead">
        {tag || topAside ? (
          <div className="br-hero__top">
            {tag ? <TagChip>{tag}</TagChip> : null}
            {topAside}
          </div>
        ) : null}
        {typeof title === "string" ? <h1 className="st-greeting">{title}</h1> : title}
        {summary ? (
          <p className="br-hero__summary">{summary}</p>
        ) : summarySkeleton > 0 ? (
          <p className="br-hero__summary" data-skel aria-hidden>
            <SkeletonLine lineHeight="calc(14px * 1.55)" barHeight={12} lines={summarySkeleton} width={summarySkeleton > 1 ? "100%" : "72%"} lastWidth="58%" />
          </p>
        ) : null}
        {meta ? <div className="br-hero__meta">{meta}</div> : null}
        {actions ? <div className="br-hero__actions">{actions}</div> : null}
      </div>
      {aside ? <div className="br-hero__aside">{aside}</div> : null}
    </section>
  );
}

/* ------------------------------------------------------------ SectionHeading */

export function SectionHeading({
  title,
  caption,
  action,
  id,
  as: Tag = "h2",
}: {
  title: string;
  caption?: ReactNode;
  action?: ReactNode;
  id?: string;
  as?: "h2" | "h3";
}) {
  return (
    <div className="br-section">
      <div className="br-section__lead">
        <Tag className="br-section__title" id={id}>
          {title}
        </Tag>
        {caption ? <span className="br-section__caption">{caption}</span> : null}
      </div>
      {action}
    </div>
  );
}

/* ------------------------------------------------------------ ActionTiles */

export type TileTone = "mint" | "sand" | "teal" | "deep";

export interface ActionTileProps {
  id: string;
  title: string;
  caption: string;
  icon: LucideIcon;
  tone?: TileTone;
  /** A route, or a handler that opens an existing flow (modal, drawer). */
  to?: string;
  onClick?: () => void;
}

export function ActionTile({ title, caption, icon: Icon, tone = "mint", to, onClick }: Omit<ActionTileProps, "id">) {
  const body = (
    <>
      <span className="br-tile__art" aria-hidden>
        <span className="br-tile__badge">
          <Icon size={15} strokeWidth={1.75} />
        </span>
        <span className="br-tile__card">
          <Icon size={30} strokeWidth={1.5} />
          <span className="br-tile__card-lines">
            <span />
            <span />
          </span>
        </span>
      </span>
      <span className="br-tile__text">
        <span className="br-tile__title">{title}</span>
        <span className="br-tile__caption">{caption}</span>
      </span>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="br-tile" data-tone={tone}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" className="br-tile" data-tone={tone} onClick={onClick}>
      {body}
    </button>
  );
}

export function ActionTiles({ tiles, label }: { tiles: ActionTileProps[]; label: string }) {
  if (tiles.length === 0) return null;
  return (
    <nav className="br-tiles" data-count={tiles.length} aria-label={label}>
      {tiles.map(({ id, ...tile }) => (
        <ActionTile key={id} {...tile} />
      ))}
    </nav>
  );
}

/* ------------------------------------------------------------ FeatureBanners */

export interface FeatureBannerProps {
  id: string;
  tag: string;
  title: string;
  body: string;
  /** One real figure, shown large and faint behind the copy. */
  figure?: number;
  action: ReactNode;
  tone?: "teal" | "sand";
}

export function FeatureBanners({ banners }: { banners: FeatureBannerProps[] }) {
  if (banners.length === 0) return null;
  return (
    <div className="br-banners" data-count={banners.length}>
      {banners.map((b) => (
        <section key={b.id} className="br-banner" data-tone={b.tone === "sand" ? "sand" : undefined} aria-label={b.title}>
          <TagChip>{b.tag}</TagChip>
          <h2 className="br-banner__title">{b.title}</h2>
          <p className="br-banner__body">{b.body}</p>
          <div className="br-banner__action">{b.action}</div>
          {b.figure !== undefined ? (
            <span className="br-banner__figure" aria-hidden>
              {b.figure}
            </span>
          ) : null}
        </section>
      ))}
    </div>
  );
}
